import { useState } from 'react';
import { toast } from 'sonner';

import {
  discardCachedRunProposal,
  renameCachedRunProposal,
  assignCachedRunProposalUser,
  unassignCachedRunProposalUser,
  removeCachedRunProposalStop,
  getCachedRunProposalReviewItems,
  getRun,
  recordCachedRunProposalDecision,
} from '@/api/runs';
import { getUsers } from '@/api/users';
import { useReviewActions } from './useReviewActions';
import { proposalPlan, withRunPlan } from '@/utils/offlineRunPlan';
import { runSendHold } from '@/utils/runSendRecovery';

const stopIds = (value) => (value?.stops || []).map(stop => stop.order_id);
const equal = (left, right) => JSON.stringify(left) === JSON.stringify(right);

export const classifyCachedRunProposal = (proposal, current) => {
  if (!current || current.missing || current.evidence_unavailable) {
    const status = current?.missing ? 'conflict' : 'unavailable';
    return Object.fromEntries((proposal.changed_sections || []).map(section => [section, status]));
  }
  const lifecycleIsDraft = current.status === 'draft'
    && !current.committed_at
    && !current.closed_at;
  const base = proposal.baseline || {};
  const desired = proposal.desired || {};
  return Object.fromEntries((proposal.changed_sections || []).map((section) => {
    if (!lifecycleIsDraft) return [section, 'conflict'];
    if (section === 'name') {
      if (current.name === desired.name) return [section, 'already_satisfied'];
      return [section, current.name === base.name ? 'safe' : 'conflict'];
    }
    if (section === 'driver') {
      if ((current.user_id || null) === (desired.user_id || null)) {
        return [section, 'already_satisfied'];
      }
      // A cloud assignment that was changed and deliberately restored is once
      // again the same shared fact the proposal started from. Review remains
      // explicit, and the cloud write still guards the currently loaded revision.
      const baseMatches = (current.user_id || null) === (base.user_id || null);
      return [section, baseMatches ? 'safe' : 'conflict'];
    }
    const currentStops = stopIds(current);
    const desiredStops = stopIds(desired);
    if (equal(currentStops, desiredStops)) return [section, 'already_satisfied'];
    return [section, equal(currentStops, stopIds(base)) ? 'safe' : 'conflict'];
  }));
};

export function useCachedRunProposalReview({ reload, owner, runRecovery }) {
  const [items, setItems] = useState([]);
  const [resolvingId, setResolvingId] = useState(null);
  const [driverUsers, setDriverUsers] = useState([]);
  const act = useReviewActions(owner, reload);
  const fetchItems = async read => {
    const localItems = await read.local(getCachedRunProposalReviewItems);
    if (!Array.isArray(localItems)) throw new Error('Invalid proposal queue');
    const users = localItems.length ? await read.cloud(getUsers, undefined).catch(() => []) : [];
    if (read.current()) setDriverUsers(users.filter(user => (user.roles || [user.role]).includes('driver') && !user.disabled_at));
    return Promise.all(localItems.map(async item => {
      let current;
      try { current = await read.cloud(getRun, item.proposal.cloud_run_id); }
      catch (error) { current = error.response?.status === 404 ? { missing: true } : { evidence_unavailable: true }; }
      return { ...item, current, section_statuses: { ...classifyCachedRunProposal(item.proposal, current),
        ...((item.proposal.dependencies || []).length ? { stop_plan: 'dependency_blocked' } : {}) } };
    }));
  };
  const run = (item, options) => (runSendHold(item.proposal) || runRecovery.owner.held('cached_proposal', item.proposal.id)) ? false
    : act(item, { key: `proposal:${item.proposal.cloud_run_id}`,
      start: () => setResolvingId(options.busyId || item.proposal.cloud_run_id), finish: () => setResolvingId(null), ...options });
  const send = async (item, operation, request) => {
    const proposal = item.proposal;
    setResolvingId(`${proposal.cloud_run_id}:${operation}`);
    try { return await runRecovery.send(owner.captureRunSend(item), {
      source_kind: 'cached_proposal', source_id: proposal.id, operation,
      ...proposalPlan(proposal), ...(request ? { request } : {}),
    }); } finally { if (owner.current()) setResolvingId(null); }
  };
  const repair = (item, local, message) => {
    const identity = proposalPlan(item.proposal);
    return run(item, { busyId: `${item.proposal.cloud_run_id}:repair`, local: config => local(withRunPlan(config, identity)),
      message, reason: 'cached_run_proposal_repaired' });
  };
  const onRename = (item, requestedName) => {
    const name = requestedName?.trim();
    if (!name || name === item.proposal.desired?.name) return false;
    return repair(item, config => renameCachedRunProposal(item.proposal.cloud_run_id, name, null, config), 'Offline proposal renamed');
  };
  const onAssignDriver = (item, id) => id && repair(item,
    config => assignCachedRunProposalUser(item.proposal.cloud_run_id, id, null, config), 'Offline driver proposal updated');
  const onUnassignDriver = item => repair(item,
    config => unassignCachedRunProposalUser(item.proposal.cloud_run_id, null, config), 'Offline driver proposal removed');
  const onRemoveStop = (item, id) => id && window.confirm('Remove this order from the offline proposal?') && repair(item,
    config => removeCachedRunProposalStop(item.proposal.cloud_run_id, id, config), 'Order removed from offline proposal');
  const onSaveAsNew = (item, requestedName = null) => {
    const proposal = item.proposal;
    const name = (requestedName === null ? window.prompt('Name for the new cloud draft:', `${proposal.desired?.name || 'Offline plan'} - recovered`) : requestedName)?.trim();
    if (!name) return false;
    if ([proposal.baseline?.name, proposal.desired?.name, item.current?.name].filter(Boolean)
      .some(value => value.trim().toLowerCase() === name.toLowerCase())) {
      toast.error('Give the recovered draft a different name'); return false;
    }
    return send(item, 'save_as_new', { name });
  };
  const onDecision = (item, section, decision) => {
    const proposal = item.proposal;
    if (!item.current || item.current.evidence_unavailable) return false;
    return run(item, { busyId: `${proposal.cloud_run_id}:${section}`,
      local: config => recordCachedRunProposalDecision(proposal.cloud_run_id, { section, decision,
        proposal_id: proposal.id, proposal_revision: proposal.proposal_revision,
        review_revision: proposal.review_revision, cloud_evidence: item.current }, config),
      reason: 'cached_run_proposal_decision', message: 'Review decision saved; no cloud change has been made yet' });
  };
  const onDiscard = item => window.confirm('Discard every offline change proposed for this cached run?') && run(item, {
    local: config => discardCachedRunProposal(item.proposal.cloud_run_id, withRunPlan(config, proposalPlan(item.proposal))),
    reason: 'cached_run_proposal_discarded', message: 'Cached-run offline changes discarded' });
  const onSynchronize = item => {
    const proposal = item.proposal;
    const decisions = Object.fromEntries(Object.entries(proposal.review_decisions || {})
      .map(([section, record]) => [section, record?.decision])
      .filter(([section, decision]) => !(section === 'stop_plan' && (proposal.dependencies || []).length)
        && ['approve', 'override', 'keep_online'].includes(decision)));
    if (!Object.keys(decisions).length) return false;
    return send(item, 'reconcile');
  };
  return { items, setItems, fetchItems, resolvingId, driverUsers, onRename, onAssignDriver, onUnassignDriver,
    onRemoveStop, onSaveAsNew, onDecision, onDiscard, onSynchronize,
    isHeld: item => !!runSendHold(item.proposal) || runRecovery.owner.held('cached_proposal', item.proposal.id) };
}
export default useCachedRunProposalReview;
